import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { adminApi, type SpendingResponse } from '@/services/adminApi';
import { formatRelativeTime } from '@/i18n/relativeTime';
import { SpendingTable, formatUsd } from '@/components/admin/SpendingTable';

interface ErrorState {
  status: number;
  message: string;
}

function parseError(err: unknown): ErrorState {
  if (err instanceof Error) {
    // The shared `api` helper throws `Error(`${status} ${body}`)`. Pull the
    // status out so we can branch the banner copy on 503 vs 502 vs other.
    const m = err.message.match(/^(\d{3})\s/);
    return { status: m ? parseInt(m[1]!, 10) : 0, message: err.message };
  }
  return { status: 0, message: String(err) };
}

export function SpendingPage(): JSX.Element {
  const { t } = useTranslation();
  const [data, setData] = useState<SpendingResponse | null>(null);
  const [error, setError] = useState<ErrorState | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Tick once a minute so the relative "as of" label stays accurate without
  // re-fetching. Same pattern as ServiceHealth.tsx.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const load = useCallback(async (mode: 'initial' | 'refresh') => {
    if (mode === 'initial') setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const res = mode === 'refresh' ? await adminApi.refreshSpending() : await adminApi.getSpending();
      setData(res);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { void load('initial'); }, [load]);

  return (
    <main className="admin-page">
      <header className="admin-page__header">
        <h1>{t('admin.spending.title')}</h1>
        <p className="muted">
          {t('admin.spending.blurb')}
        </p>
      </header>

      {loading && <p className="muted" role="status">{t('common.loading')}</p>}

      {error && error.status === 503 && (
        <p className="error" role="alert" data-testid="spend-error-503">
          <Trans i18nKey="admin.spending.error503" components={{ code: <code /> }} />
        </p>
      )}
      {error && error.status === 502 && (
        <p className="error" role="alert" data-testid="spend-error-502">
          {t('admin.spending.error502', { message: error.message })}
          <button type="button" className="btn-ghost" onClick={() => void load('initial')}>{t('common.retry')}</button>
        </p>
      )}
      {error && error.status !== 502 && error.status !== 503 && (
        <p className="error" role="alert" data-testid="spend-error">
          {error.message}
        </p>
      )}

      {data && (
        <>
          <section className="admin-card spending-summary">
            <div className="admin-card__row">
              <h2 className="admin-card__title">{t('admin.spending.totals')}</h2>
              <button
                type="button"
                className="btn"
                onClick={(): void => void load('refresh')}
                disabled={refreshing}
                data-testid="spend-refresh"
              >
                {refreshing ? t('admin.spending.refreshing') : t('admin.spending.refresh')}
              </button>
            </div>
            <dl className="spending-totals">
              <div>
                <dt>{t('admin.spending.mtd')}</dt>
                <dd data-testid="spend-totals-mtd">{formatUsd(data.totals.mtdCostUsd)}</dd>
              </div>
              <div>
                <dt>{t('admin.spending.projected')}</dt>
                <dd data-testid="spend-totals-proj">{formatUsd(data.totals.projectedMonthlyUsd)}</dd>
              </div>
            </dl>
            <p className="muted spending-asof">
              <Trans
                i18nKey="admin.spending.snapshot"
                components={{ code: <code /> }}
                values={{ when: formatRelativeTime(t, data.asOf, nowMs), version: data.pricingTableVersion }}
              />
              {data.totals.hasUnknownMachineTypes && (
                <span className="text-dim" data-testid="spend-totals-unknown-flag">
                  {t('admin.spending.unknownFlag')}
                </span>
              )}
            </p>
          </section>

          <SpendingTable apps={data.apps} />

          <footer className="muted spending-footer" data-testid="spend-egress-disclaimer">
            {t('admin.spending.egressNote')}
          </footer>
        </>
      )}
    </main>
  );
}
